// src/ui/steps/step4-festival-loondienst.jsx — Festival loondienst-flow eindscherm
// Toont bruto fulltime + gewogen gemiddeld bruto/maand + per-maand uitsplitsing.
// Geen openingsbod (F2-F.14), geen Word-export (Q.7.5).
import React from "react";
import { FW, FwButton, FwCard, FwInlineBanner, StepHeader, ResultaatSamenvatting } from "../primitives";
import { calc } from "../../lib/calc";
import { aiHelp } from "../../lib/ai";
import { Aanname, Regel, RolOmschrijvingDetail } from "./step4-result";
import { exportFestivalLoondienst } from "../../lib/docx-export";

export const StepFestivalLoondienstResult = ({ state, setState, back, restart }) => {
  const huidigeRol = state.rollen?.[0];
  const result = huidigeRol ? calc.berekenFestivalLoondienst(huidigeRol) : null;

  const [copied, setCopied] = React.useState(false);
  const [sharing, setSharing] = React.useState(false);
  const [signalen, setSignalen] = React.useState([]);
  const [loadingSig, setLoadingSig] = React.useState(false);
  const [sigError, setSigError] = React.useState(false);

  React.useEffect(() => {
    if (!huidigeRol || !state.omschrijving) return;
    const ctrl = new AbortController();
    setLoadingSig(true);
    aiHelp.signalOnderhandelpunten(
      state.omschrijving,
      { signal: ctrl.signal },
      {
        rol: huidigeRol.rol,
        niveau: huidigeRol.niveau,
        periodiek: huidigeRol.periodiek,
        dienstverband: huidigeRol.dienstverband || [],
      },
    ).then(r => {
      if (ctrl.signal.aborted) return;
      setLoadingSig(false);
      if (r?.signalen) setSignalen(r.signalen);
      else setSigError(true);
    });
    return () => ctrl.abort();
  }, []);

  if (!huidigeRol || !result) {
    return <div style={{ padding: 40, color: FW.navy }}>Geen berekening mogelijk — ga terug en vul je dienstverband in.</div>;
  }

  const shareLink = () => {
    const json = btoa(unescape(encodeURIComponent(JSON.stringify(state))));
    const url = `${location.origin}${location.pathname}#berekening=${json}`;
    navigator.clipboard.writeText(url);
    setSharing(true);
    setTimeout(() => setSharing(false), 2000);
  };
  const copyEmail = () => {
    const txt = generateEmailFestivalLd(state, huidigeRol, result);
    navigator.clipboard.writeText(txt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const downloadWord = () => exportFestivalLoondienst({ state, huidigeRol, result, signalen });

  return (
    <div className="result-page">
      <StepHeader
        tag="Salarisindicatie"
        title="Jouw bruto maandsalaris"
        sub="Op basis van de CAO Toneel & Dans (schalen 1–12) en jouw dienstverband. Voor loondienst rekenen we niet met een openingsbod, maar met het bruto maandsalaris dat past bij jouw schaal, periodiek en deeltijd-percentage."
        onBack={back}
      />

      <ResultaatSamenvatting
        isFestival={true}
        vorm={state.vorm}
        rol={huidigeRol.rol}
        dep={huidigeRol.dep}
        niveau={huidigeRol.niveau}
      />

      <div className="row-2col" style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 24, alignItems: "start" }}>
        <div>
          <div style={{ background: FW.white, border: `1.5px solid ${FW.gray100}`, borderRadius: 20, padding: 24 }}>
            {result.perBlok.map((b, i) => (
              <div key={b.id || i} style={{ padding: "8px 0" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 15, color: FW.navy }}>
                  <span>
                    Blok {i + 1}: <b>{b.maanden}</b> mnd × <b>{b.pct}%</b> = {calc.fmt(b.brutoMaand)}<span style={{ color: FW.gray500 }}> /mnd</span>
                  </span>
                  <span style={{ fontWeight: 700 }}>{calc.fmt(b.brutoBlok)}</span>
                </div>
              </div>
            ))}
            <div style={{ height: 1, background: FW.gray100, margin: "12px 0" }} />
            <Regel label={`Totaal periode · ${result.totaalMaanden} mnd`} bedrag={result.totaalBruto} strong />
            <Regel label={`Gemiddeld /maand (gewogen)`} bedrag={result.gemBruto} />
          </div>

          <div style={{ marginTop: 32, fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Aannames</div>
          <div style={{ fontSize: 14, color: FW.navy2, marginBottom: 16 }}>
            Klik op een aanname om de onderbouwing te zien of de waarde aan te passen.
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            {state.omschrijving && (
              <Aanname
                label="Omschrijving"
                waarde={state.omschrijving.length > 80 ? state.omschrijving.slice(0, 80).trim() + "…" : state.omschrijving}
                uitleg="Dit is de tekst die je hebt ingevuld als beschrijving van de opdracht. AI gebruikt deze tekst om rol en schaal te suggereren."
                onEdit={() => { back(); back(); back(); }}
                detail={
                  <div style={{ background: FW.white, border: `1px solid ${FW.gray100}`, borderRadius: 10, padding: 12, fontSize: 14, color: FW.navy2, lineHeight: 1.55, whiteSpace: "pre-wrap", marginBottom: 12 }}>
                    {state.omschrijving}
                  </div>
                }
              />
            )}
            <Aanname
              label="Type opdracht"
              waarde="Filmfestival — loondienst"
              uitleg="Voor loondienst bij filmfestivals geldt de CAO Toneel & Dans met 12 schalen. Het bruto maandsalaris is een ondergrens; de werkgever mag hoger inschalen."
              onEdit={() => restart()}
            />
            <Aanname
              label="Rol / functie"
              waarde={`${huidigeRol.rol} · ${huidigeRol.dep}`}
              uitleg="Op basis van jouw omschrijving (of jouw keuze in de rollen-lijst) is deze rol gekozen uit de Matrix Functies Filmfestivals."
              onEdit={() => { back(); back(); back(); }}
              detail={<RolOmschrijvingDetail rol={huidigeRol.rol} dep={huidigeRol.dep} isFestival={true} />}
            />
            <Aanname
              label="Schaal"
              waarde={`Schaal ${huidigeRol.niveau}${result.tariefRij?.rol_voorbeeld ? ` (${result.tariefRij.rol_voorbeeld})` : ""}`}
              uitleg={`Schaal ${huidigeRol.niveau} (CAO Toneel & Dans) bepaalt de salaristabel waaruit jouw bruto fulltime maandsalaris komt.`}
              onEdit={() => { back(); back(); }}
            />
            <Aanname
              label="Periodiek"
              waarde={`Periodiek ${result.periodiek}`}
              uitleg={`Het aantal jaren ervaring binnen schaal ${huidigeRol.niveau}. Elk jaar in dezelfde schaal stijg je één periodiek op tot de top is bereikt.`}
              onEdit={() => back()}
            />
            <Aanname
              label="Dienstverband"
              waarde={`${result.totaalMaanden} maanden in ${result.perBlok.length} blok${result.perBlok.length === 1 ? "" : "ken"}`}
              uitleg="Jouw dienstverband is opgesplitst in blokken van maanden × deeltijd-percentage. Het gemiddelde bruto/maand is gewogen op aantal maanden."
              onEdit={() => back()}
            />
          </div>
        </div>

        <div className="aside-card" style={{ display: "grid", gap: 16, position: "sticky", top: 20 }}>
          <FwCard padded={false} style={{ padding: 22, background: FW.cream }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 10 }}>Goed om te benoemen</div>
            <ul style={{ fontSize: 14, color: FW.navy2, margin: 0, paddingLeft: 16, lineHeight: 1.55, display: "grid", gap: 8 }}>
              <li>Dit is een <b>ondergrens</b> volgens de CAO Toneel & Dans. De werkgever mag <b>hoger inschalen</b> op basis van ervaring of complexiteit.</li>
              <li><b>Vakantiegeld, pensioen, vakantiedagen</b> en eventuele eindejaarsuitkering komen <b>bovenop</b> dit bruto.</li>
              <li>Bij <b>variabele inzet</b> per maand kun je vragen om compensatie in tijd of een vaste opslag voor onregelmatige uren.</li>
            </ul>
          </FwCard>

          {loadingSig && (
            <div style={{ padding: 16, background: FW.beige2, borderRadius: 14, fontSize: 14, color: FW.navy, display: "inline-flex", alignItems: "center", gap: 10 }}>
              <i className="ri-sparkling-2-line" />
              Aandachtspunten voor je gesprek worden geladen…
            </div>
          )}
          {!loadingSig && sigError && (
            <FwInlineBanner type="warning" onDismiss={() => setSigError(false)}>
              <div style={{ fontWeight: 700 }}>Aandachtspunten konden niet geladen worden.</div>
            </FwInlineBanner>
          )}
          {!loadingSig && (
            <FwCard padded={false} style={{ padding: 22, background: FW.cream, border: `1.5px solid ${FW.yellow}` }}>
              <h3 style={{ margin: "0 0 6px", fontSize: 14, fontWeight: 700, color: FW.navy, display: "inline-flex", alignItems: "center", gap: 6 }}>
                <i className="ri-chat-3-line" /> Aandachtspunten voor je gesprek
              </h3>
              <p style={{ fontSize: 13, color: FW.navy2, margin: "0 0 8px", lineHeight: 1.45 }}>
                Dit bruto-bedrag is de ondergrens. In een loondienst-gesprek gaat het verder over de secundaire voorwaarden:
              </p>
              <ul style={{ margin: "0 0 12px", paddingLeft: 16, display: "grid", gap: 4 }}>
                <li style={{ fontSize: 13, color: FW.navy2, lineHeight: 1.45 }}><b>Vakantiegeld</b> (8%) en <b>eindejaarsuitkering</b> — meestal bovenop het bruto.</li>
                <li style={{ fontSize: 13, color: FW.navy2, lineHeight: 1.45 }}><b>Pensioenbijdrage werkgever</b> — vraag naar de regeling en de werkgeversbijdrage.</li>
                <li style={{ fontSize: 13, color: FW.navy2, lineHeight: 1.45 }}><b>Compensatie in tijd</b> voor festivaldagen, weekend- en avondwerk.</li>
                <li style={{ fontSize: 13, color: FW.navy2, lineHeight: 1.45 }}><b>Reiskosten</b> en eventueel <b>thuiswerkvergoeding</b>.</li>
                <li style={{ fontSize: 13, color: FW.navy2, lineHeight: 1.45 }}><b>Vertrouwenspersoon</b> en sociale veiligheid — moet bij het festival belegd zijn.</li>
              </ul>
              {signalen.length > 0 && (
                <>
                  <p style={{ fontSize: 13, fontWeight: 700, color: FW.navy, margin: "0 0 6px", lineHeight: 1.45 }}>
                    Specifiek uit jouw omschrijving:
                  </p>
                  <ul style={{ margin: 0, paddingLeft: 16, display: "grid", gap: 6 }}>
                    {signalen.map((s, i) => (
                      <li key={i} style={{ fontSize: 13, color: FW.navy2, lineHeight: 1.45 }}>
                        <strong style={{ color: FW.navy }}>{s.factor}</strong> — {s.toelichting}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </FwCard>
          )}
        </div>
      </div>

      <FwCard tint="navy" padded={false} className="summary-card" style={{ padding: "40px 44px", marginTop: 32 }}>
        <div className="summary-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 32, alignItems: "end" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.yellow, opacity: .9 }}>Bruto fulltime</div>
            <div className="summary-value" style={{ fontSize: 48, fontWeight: 700, color: "#fff", lineHeight: 1, marginTop: 8, fontVariantNumeric: "tabular-nums" }}>
              {calc.fmtInt(result.brutoFulltime)}
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,.65)", marginTop: 6 }}>
              Schaal {huidigeRol.niveau} · periodiek {result.periodiek} · /maand
            </div>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.yellow, opacity: .9 }}>Periode</div>
            <div className="summary-value" style={{ fontSize: 48, fontWeight: 700, color: "#fff", lineHeight: 1, marginTop: 8, fontVariantNumeric: "tabular-nums" }}>
              {result.totaalMaanden}
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,.65)", marginTop: 6 }}>
              maanden in totaal
            </div>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.yellow, opacity: .9 }}>Gemiddeld /maand</div>
            <div className="summary-value" style={{ fontSize: 48, fontWeight: 700, color: "#fff", lineHeight: 1, marginTop: 8, fontVariantNumeric: "tabular-nums" }}>
              {calc.fmtInt(result.gemBruto)}
            </div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,.65)", marginTop: 6 }}>
              Gewogen op aantal maanden
            </div>
          </div>
        </div>
      </FwCard>

      <FwCard padded={false} style={{ marginTop: 24, padding: 22, background: FW.mint }} className="hide-print">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0, flex: "1 1 260px" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: FW.navy, marginBottom: 4, display: "inline-flex", alignItems: "center", gap: 6 }}>
              <i className="ri-send-plane-line" /> Deel je salarisindicatie
            </div>
            <div style={{ fontSize: 14, color: FW.navy2, lineHeight: 1.4 }}>
              Deel via een link, kopieer een gespreksvoorbereiding, of download als pdf.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <FwButton variant="navy" icon="ri-link" arrow={false} onClick={shareLink}>
              {sharing ? "Link gekopieerd ✓" : "Kopieer deelbare link"}
            </FwButton>
            <FwButton variant="secondary" icon="ri-mail-line" arrow={false} onClick={copyEmail}>
              {copied ? "Tekst gekopieerd ✓" : "Kopieer gespreksvoorbereiding"}
            </FwButton>
            <FwButton variant="secondary" icon="ri-file-word-2-line" arrow={false} onClick={downloadWord}>
              Download als Word
            </FwButton>
          </div>
        </div>
      </FwCard>

      <div style={{ marginTop: 32, display: "flex", justifyContent: "space-between" }} className="hide-print step-nav">
        <FwButton variant="ghost" arrow={false} onClick={back}>← Pas dienstverband aan</FwButton>
        <FwButton variant="secondary" arrow={false} onClick={restart}>Nieuwe berekening</FwButton>
      </div>
    </div>
  );
};

function generateEmailFestivalLd(state, rol, result) {
  const blokRegels = result.perBlok
    .map((b, i) => `  Blok ${i + 1}: ${b.maanden} maanden × ${b.pct}% = ${calc.fmt(b.brutoMaand)} bruto/maand`)
    .join("\n");
  return `Beste [werkgever],

Ter voorbereiding op ons gesprek over mijn aanstelling als ${rol.rol} (${rol.dep}) bij jullie filmfestival.

Op basis van de CAO Toneel & Dans (schaal ${rol.niveau}, periodiek ${result.periodiek}):

• Bruto fulltime maandsalaris: ${calc.fmt(result.brutoFulltime)}
• Periode dienstverband: ${result.totaalMaanden} maanden
• Gemiddeld bruto/maand (gewogen): ${calc.fmt(result.gemBruto)}
• Totaal bruto over de periode: ${calc.fmt(result.totaalBruto)}

Onderbouwing per dienstverband-blok:
${blokRegels}

Naast het bruto-salaris bespreek ik graag de secundaire arbeidsvoorwaarden: vakantiegeld, eindejaarsuitkering, pensioenregeling, vergoeding voor reiskosten, en compensatie in tijd voor festival- en avondwerk. Ook zou ik graag horen hoe sociale veiligheid (vertrouwenspersoon) bij jullie is georganiseerd.

Met vriendelijke groet,
[jouw naam]`;
}
